import { Capacitor } from "@capacitor/core";
import { Network } from "@capacitor/network";

/**
 * Keep this version same as in the pdftron-offline-sw.js file
 * If you change the version there, change it here too, and vice versa, so the SW gets updated
 */
const CACHE_VERSION = "v4";
/**
 * Register the PDFTron Service Worker
 * The service worker is used to cache the PDFTron library and provide offline support
 * The service worker only intercepts requests when offline, letting them pass through when online
 * @returns {void}
 */
export const registerPDFTronServiceWorker = async (): Promise<void> => {
  if ("serviceWorker" in navigator) {
    const swPath = `/pdftron-offline-sw.js?v=${CACHE_VERSION}`;

    const registerSW = async () => {
      try {
        console.log("Registering PDFTron Service Worker...");
        const registration = await navigator.serviceWorker.register(swPath, {
          scope: "/webviewer/",
          updateViaCache: "none",
        });

        await navigator.serviceWorker.ready;
        console.log("PDFTron Service Worker is ready");

        return registration;
      } catch (error) {
        console.error("Failed to register the PDFTron Service Worker:", error);
        throw error;
      }
    };

    const checkIfRegistered = async () => {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const pdftronSW = registrations.find((reg) =>
          reg.active?.scriptURL.endsWith(swPath)
        );

        if (pdftronSW) {
          console.log("PDFTron Service Worker already registered.");
          return pdftronSW;
        }

        return null;
      } catch (error) {
        console.error("Error checking service worker registration:", error);
        return null;
      }
    };

    const sendNetworkStatusToSW = async (isOnline: boolean) => {
      try {
        const registration = await checkIfRegistered();
        if (registration?.active) {
          registration.active.postMessage({
            isOnline,
            type: "NETWORK_STATUS",
          });
        }
      } catch (error) {
        console.error("Error sending network status to service worker:", error);
      }
    };

    const initializeServiceWorker = async () => {
      try {
        await registerSW();
        if (Capacitor.isPluginAvailable("Network")) {
          const { connected } = await Network.getStatus();
          await sendNetworkStatusToSW(connected);
        } else {
          // Fallback to browser's navigator.onLine when Network plugin is unavailable
          console.log(
            "Network plugin unavailable, using browser's navigator.onLine"
          );
          await sendNetworkStatusToSW(navigator.onLine);
        }
      } catch (error) {
        console.error("Error initializing PDFTron Service Worker:", error);
      }
    };

    // Register the service worker immediately - it should always be registered
    void initializeServiceWorker();

    const handleNetworkChange = async (connected: boolean) => {
      await sendNetworkStatusToSW(connected);
    };

    // Listen to network changes and update service worker
    if (Capacitor.isPluginAvailable("Network")) {
      void Network.getStatus().then(({ connected }) => {
        console.log(
          "Initial network status:",
          connected ? "ONLINE" : "OFFLINE"
        );
        void handleNetworkChange(connected);
      });

      void Network.addListener("networkStatusChange", (status) => {
        console.log(
          "Network status changed via Capacitor:",
          status.connected ? "ONLINE" : "OFFLINE"
        );
        void handleNetworkChange(status.connected);
      });
    } else {
      // Fallback to browser's online/offline events when Network plugin is unavailable
      console.log(
        "Network plugin unavailable, using browser's online/offline events"
      );

      // Set initial status
      void handleNetworkChange(navigator.onLine);
    }
  }
};
